"use client";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toWebp } from "@/utils/common";

interface TimelineProps {
  data?: any;
}

const Timeline: React.FC< TimelineProps> = ({ data  }) =>  {
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";
  const slides = data?.slides || [];

  const [active, setActive] = useState<number>(0);
  const sectionsRef = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number((entry.target as HTMLElement).dataset.index);
            if (!Number.isNaN(idx)) {
              setActive(idx);
            }
          }
        });
      },
      { root: null, rootMargin: "-30% 0px -30% 0px", threshold: 0.1 }
    );

    sectionsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      sectionsRef.current.forEach((el) => {
        if (el) observer.unobserve(el);
      });
      observer.disconnect();
    };
  }, []);

  return (
    <main className="w-full">
      {/* Sticky viewport */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {slides.map((slide: any, idx: number) => {
          const desktopImg = toWebp(
            slide?.image?.data?.attributes?.url &&
            baseUrl + slide.image.data.attributes.url
          );

          const ipadImg = toWebp(
            slide?.ipad_image?.data?.attributes?.url &&
            baseUrl + slide.ipad_image.data.attributes.url
          );

          const mobileImg = toWebp(
            slide?.mobile_image?.data?.attributes?.url &&
            baseUrl + slide.mobile_image.data.attributes.url
          );

          // Determine which background pair to use:
          // - If slide.cardColor is provided ('blue' | 'green'), use that.
          // - Otherwise fall back to index parity: even -> blue (t1/t2), odd -> green (g1/g2).
          const cardColorFromData =
            typeof slide?.cardColor === "string"
              ? slide.cardColor.toLowerCase()
              : undefined;
          const isBlueCard =
            cardColorFromData === "blue" || (!cardColorFromData && idx % 2 === 0);

          // Allow override with slide-specific bg images if available
          const leftBg =
            slide?.bg_left ||
            (isBlueCard ? "/images/about/t1.png" : "/images/about/g1.png");
          const rightBg =
            slide?.bg_right ||
            (isBlueCard ? "/images/about/t2.png" : "/images/about/g2.png");

          return (
            <motion.div
              key={slide?.id || idx}
              animate={{
                opacity: active === idx ? 1 : 0,
                scale: active === idx ? 1 : 1.05,
              }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className="absolute inset-0 top-0 flex items-start justify-center"
              style={{
                zIndex: active === idx ? 10 : 0,
                pointerEvents: active === idx ? "auto" : "none",
              }}
            >
              {/* Desktop Background */}
              {desktopImg && (
                <Image
                  src={desktopImg}
                  alt={`Timeline slide ${idx + 1}`}
                  fill
                  className="hidden xl:block object-contain"
                  sizes="(min-width: 1024px) 100vw"
                  priority={idx === 0}
                />
              )}

              {/* iPad / Tablet Background */}
              {ipadImg && (
                <Image
                  src={ipadImg}
                  alt={`Timeline slide ${idx}`}
                  fill
                  className="hidden sm:block xl:hidden object-contain"
                  sizes="(min-width: 640px) and (max-width: 1023px) 100vw"
                  priority={idx === 0}
                />
              )}

              {/* Mobile Background */}
              {mobileImg && (
                <Image
                  src={mobileImg}
                  alt={`Timeline slide ${idx + 1}`}
                  fill
                  className="block sm:hidden object-cover max-h-[540px] mt-[80px] sm:mt-0"
                  sizes="(max-width: 639px) 100vw"
                  priority={idx === 0}
                />
              )}

              {/* Text overlay */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={
                  active === idx ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }
                }
                transition={{
                  duration: 0.8,
                  ease: "easeOut",
                  delay: active === idx ? 0.2 : 0,
                }}
                className={`relative xl:top-[100px] top-text-history md:top-[380px] 
    max-w-[calc(100%-40px)] min-[1500px]:top-[140px] z-20 border 
    sm:p-[32px_21px] p-[20px_8px] sm:max-w-[726px] mx-auto bg-white/90 backdrop-blur-sm
    before:content-[''] before:absolute sm:before:bottom-[-173px] before:bottom-[-123px] before:left-1/2 
    before:-translate-x-1/2 sm:before:h-[173px] before:h-[123px] before:w-[1px] 
    after:content-[''] after:absolute sm:after:bottom-[-173px] after:bottom-[-123px] after:left-1/2 
    after:-translate-x-1/2 after:h-[9px] after:w-[24px] after:bg-no-repeat after:bg-contain after:bg-center 
    after:rounded-full min-h-[118px] max-h-[118px]
    ${idx % 2 === 0
                    ? "border-blue-100 before:bg-blue-100 after:bg-[url('/images/about/ellipse.png')]"
                    : "border-green-100 before:bg-green-100 after:bg-[url('/images/about/ellipse-green.png')]"}`}
              >
                {/* Only render the chosen pair per card (left + right). */}
                <Image
                  src={leftBg}
                  alt={`${isBlueCard ? "blue" : "green"}-left`}
                  width={106}
                  height={106}
                  className="w-[106px] h-[106px] object-cover absolute left-0 top-0"
                />

                <Image
                  src={rightBg}
                  alt={`${isBlueCard ? "blue" : "green"}-right`}
                  width={130}
                  height={130}
                  className="w-[130px] h-[130px] object-cover mt-auto absolute right-0 bottom-0 "
                />

                <p className="text-primary-text text-heading-6 font-bold text-center ">
                  {slide?.display_title}
                </p>
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Scroll trigger sections */}
      {slides.map((_: any, idx: number) => (
        <section
          key={`section-${idx}`}
          data-index={idx}
          ref={(el) => {
            sectionsRef.current[idx] = el as HTMLDivElement | null;
          }}
          className="h-screen flex items-center justify-center"
        />
      ))}
    </main>
  );
};
export default Timeline;